import React, { useMemo } from "react";
import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import Checkbox from "components/checkbox";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdEdit, MdDelete } from "react-icons/md";

const columnHelper = createColumnHelper();

export default function GenresTable(props) {
  const { tableData } = props;
  const [sorting, setSorting] = React.useState([]);
  
  const columns = useMemo(() => [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">GENRE NAME</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <Checkbox defaultChecked={false} colorScheme="brandScheme" me="10px" />
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">DATE ADDED</p>
      ),
      cell: (info) => {
        const date = new Date(info.getValue());
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {date.toLocaleDateString()}
          </p>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ACTIONS</p>
      ),
      cell: (info) => {
        const genre = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => props.onEdit(genre)}
              className="linear rounded-full bg-lightPrimary p-2 text-brand-500 transition duration-200 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20"
            >
              <MdEdit className="h-4 w-4" />
            </button>
            <button
              onClick={() => props.onDelete(genre.id)}
              className="linear rounded-full bg-lightPrimary p-2 text-red-500 transition duration-200 hover:bg-gray-200 dark:bg-navy-700 dark:text-red-500 dark:hover:bg-white/20"
            >
              <MdDelete className="h-4 w-4" />
            </button>
          </div>
        );
      },
    }),
  ], [props.onEdit, props.onDelete]);

  const table = useReactTable({
    data: tableData || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Check Table (Genres)
        </div>
        <CardMenu />
      </header>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                  >
                    <div className="items-center justify-between text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted()] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="min-w-[150px] border-white/0 py-3 pr-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={3} className="py-4 text-center text-gray-500">
                  No genres found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {table.getPageCount() > 1 && (
        <div className="mt-4 flex w-full items-center justify-between px-4 pb-2">
          <div className="text-sm text-gray-600 dark:text-white">
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getPrePaginationRowModel().rows.length
            )}{" "}
            of {table.getPrePaginationRowModel().rows.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="linear rounded-[20px] bg-lightPrimary px-4 py-2 text-sm font-bold text-brand-500 transition duration-200 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20"
            >
              Prev
            </button>
            <div className="text-sm font-bold text-navy-700 dark:text-white">
              {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </div>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="linear rounded-[20px] bg-brand-500 px-4 py-2 text-sm font-bold text-white transition duration-200 hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
